/*
 * Brewapp (Checkout Brew) protection controls for the cart drawer.
 *
 * Two elements, both rendered by snippets/brewapp-protection.liquid:
 *   <brewapp-protection>  the Checkout+ row and its info modal
 *   <brewapp-optout>      the "continue without" control below the checkout button
 *
 * The fee shown in the row is rendered by Liquid, so nothing here computes or formats
 * money. All this file does is write the cart attributes and run the modal.
 *
 * a-cart-drawer-v14.js refreshes the drawer by replacing its innerHTML, which destroys and
 * rebuilds both elements on every cart change. Two consequences worth remembering:
 *   - the <script> tag in the replaced markup does not re-run, but custom elements upgrade
 *     on insertion by themselves, so the new nodes are live without any re-init hook;
 *   - anything attached outside the element (the Esc handler, an open modal) has to be
 *     released in disconnectedCallback or it accumulates on every quantity change.
 */

(function () {
  'use strict';

  /*
   * Both keys drive the same thing on the Brewapp side. `_auto_add_shipping_protection` is
   * the original; `orderPlusChecked` was added later and is equivalent. Confirmed by the
   * merchant on 2026-09-02 — write both until the vendor retires one.
   */
  var ATTRIBUTE_KEYS = ['_auto_add_shipping_protection', 'orderPlusChecked'];

  /* Locale-aware root ('/', '/en-gb/', ...). The 2026-03 version hardcoded '/checkout',
     which drops the locale prefix — this store runs t-lab translate with several locales. */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function writeOptState(optIn) {
    var value = optIn ? 'true' : 'false';
    var attributes = {};

    ATTRIBUTE_KEYS.forEach(function (key) {
      attributes[key] = value;
    });

    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart/update.js responded ' + response.status);
      return response.json();
    });
  }

  function goToCheckout() {
    window.location.href = root() + 'checkout';
  }

  /*
   * The app's own pricing endpoint. Not an app proxy under /apps/, which would have been the
   * tidier shape — same origin, no preflight, and immune to ad blockers that drop requests to
   * unfamiliar hosts. The vendor serves it from their own domain with Access-Control-Allow-
   * Origin: *, which works; if blockers ever start eating it, this is the line to change.
   */
  var FEE_ENDPOINT = 'https://app.checkoutbrew.com/api/order-plus-ladder';

  /*
   * One request per distinct cart, keyed by the body Liquid rendered — identical markup means
   * an identical cart. a-cart-drawer-v14.js rebuilds the drawer on every mutation, so without
   * this a customer nudging a quantity would fire a request per click. Failures are not
   * remembered, so a blip is retried on the next render rather than cached for the session.
   */
  var feeRequests = Object.create(null);

  function fetchFee(body) {
    if (feeRequests[body]) return feeRequests[body];

    feeRequests[body] = fetch(FEE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body
    })
      .then(function (response) {
        if (!response.ok) throw new Error('order-plus-ladder responded ' + response.status);
        return response.json();
      })
      .catch(function (error) {
        delete feeRequests[body];
        throw error;
      });

    return feeRequests[body];
  }

  /*
   * The decline link is a sibling element in another part of the footer, not a child of the
   * row, so hiding the offer has to reach across to it. Kept to this one helper rather than
   * letting either component know the other's internals.
   */
  function setOptoutHidden(hidden) {
    var optout = document.querySelector('brewapp-optout');
    if (optout) optout.hidden = hidden;
  }

  /* The response gives an amount and a currency code, not a formatted string. */
  function formatFee(amount, currencyCode) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2) + ' ' + (currencyCode || '');
    }
  }

  if (!customElements.get('brewapp-protection')) {
    customElements.define(
      'brewapp-protection',
      class BrewappProtection extends HTMLElement {
        connectedCallback() {
          this.listeners = new AbortController();

          var infoButton = this.querySelector('.brewapp-protection-container--info-icon');
          if (infoButton) {
            infoButton.addEventListener('click', this.openModal.bind(this), {
              signal: this.listeners.signal
            });
          }

          /* The default opt-in is <brewapp-optin>'s job, not this element's. It used to
             live here, which meant it only ran when the drawer footer was on the page. */

          this.applyFee();
        }

        /*
         * Asks the app what it will charge, and shows that.
         *
         * Nothing here works the figure out. Two things make it impossible from the theme: the
         * app splits the cart by selling plan and prices each group against the ladder on its
         * own, so the fee is a sum of rungs rather than the rung for the whole subtotal; and it
         * chooses the rung in the shop currency, which stopped being readable from Liquid when
         * the ladder moved to opaque variant titles ("Tier 001" … "Tier 250").
         *
         * Liquid rendered the request body; this posts it and prints `total`.
         */
        applyFee() {
          var priceEl = this.querySelector('.brewapp-protection-container--price');
          var requestEl = this.querySelector('.brewapp-protection-request');
          if (!priceEl || !requestEl) return;

          var body = requestEl.textContent.trim();
          if (!body) return;

          var row = this;

          fetchFee(body)
            .then(function (data) {
              if (!data || typeof data.total !== 'number') {
                throw new Error('no usable total in the response');
              }

              /* Nothing to sell — an empty cart, or one holding only free lines. The endpoint
                 answers 0 with an empty `groups` for both. Hide the offer rather than price it
                 at zero, and take the decline link with it: there is nothing to decline. */
              if (data.total <= 0) {
                row.hidden = true;
                setOptoutHidden(true);
                return;
              }

              row.hidden = false;
              setOptoutHidden(false);
              priceEl.classList.remove('is-loading');
              priceEl.textContent = formatFee(data.total, data.currencyCode);

              /* The app owns the wording: these are the Block title and Block subtitle from
                 the Order Plus block, so editing them there reaches the drawer without a
                 deploy. The translations still render first and remain the fallback for a
                 failed request — keep them matching, or the text changes under the customer.
                 The modal's benefit list is not in the response and stays on translations. */
              var nameEl = row.querySelector('.brewapp-protection-container--name');
              var subtitleEl = row.querySelector('.brewapp-protection-container--text');

              if (nameEl && data.title) nameEl.textContent = data.title;
              if (subtitleEl && data.subtitle) subtitleEl.textContent = data.subtitle;
            })
            .catch(function (error) {
              /* Keep the row and drop only the amount. Removing the row would take the decline
                 link with it while the opt-in attribute stays 'true', so the customer would be
                 charged at checkout for something they were never shown and could not refuse. */
              priceEl.classList.remove('is-loading');
              priceEl.textContent = '';
              console.error('Brewapp: could not price the protection', error);
            });
        }

        disconnectedCallback() {
          if (this.listeners) this.listeners.abort();
          this.closeModal();
        }

        /*
         * Called from the checkout handler in a-cart-drawer-v14.js. Returns a promise so
         * the handler can await it before redirecting. Resolves rather than rejects on
         * failure: a customer who cannot reach checkout is a worse outcome than a missing
         * attribute, and the same trade-off is made on the opt-out path below.
         */
        optIn() {
          if (this.dataset.optState === 'true') return Promise.resolve();

          return writeOptState(true)
            .then(
              function () {
                this.dataset.optState = 'true';
              }.bind(this)
            )
            .catch(function (error) {
              console.error('Brewapp: could not record the opt-in', error);
            });
        }

        openModal() {
          this.closeModal();

          var template = this.querySelector('.brewapp-protection-modal-template');
          if (!template) return;

          /* The template carries the whole modal box, close button included, so every
             string in it comes from the locale file instead of a JS literal. */
          var overlay = document.createElement('div');
          overlay.className = 'brewapp-protection-modal-overlay brewapp-protection-modal';
          overlay.setAttribute('role', 'dialog');
          overlay.setAttribute('aria-modal', 'true');
          overlay.appendChild(template.content.cloneNode(true));

          this.modal = overlay;
          this.modalListeners = new AbortController();
          var signal = this.modalListeners.signal;
          var closeModal = this.closeModal.bind(this);

          var close = overlay.querySelector('.brewapp-protection-modal-close');
          if (close) {
            close.addEventListener('click', closeModal, { signal: signal });
          }
          overlay.addEventListener(
            'click',
            function (event) {
              if (event.target === overlay) closeModal();
            },
            { signal: signal }
          );
          document.addEventListener(
            'keydown',
            function (event) {
              if (event.key === 'Escape') closeModal();
            },
            { signal: signal }
          );

          document.body.appendChild(overlay);
          if (close) close.focus();
        }

        closeModal() {
          if (this.modalListeners) {
            this.modalListeners.abort();
            this.modalListeners = null;
          }
          if (this.modal) {
            this.modal.remove();
            this.modal = null;
          }
        }
      }
    );
  }

  /*
   * Records the default opt-in, once per cart, for customers who never open the drawer.
   *
   * Most of this theme's buy flows — the build-a-box pages, the colour pickers — add a
   * product and go straight to checkout, and several of them run on a page that loaded with
   * an empty cart, so the drawer footer was never rendered. Anything living inside that
   * footer misses those customers entirely, which is why this element sits outside it and
   * renders on every page.
   *
   * Liquid only renders it while the choice is still unmade, so there is nothing here to
   * undo an explicit opt-out: once the attribute is 'false', this element stops existing.
   */
  /* One write per page, whatever puts the element there. The cart drawer section renders it,
     and so do the layouts that have no drawer; a page that somehow got both would otherwise
     fire two identical writes. Also covers connectedCallback firing again if the node moves. */
  var optinWritten = false;

  if (!customElements.get('brewapp-optin')) {
    customElements.define(
      'brewapp-optin',
      class BrewappOptin extends HTMLElement {
        connectedCallback() {
          if (optinWritten) return;
          optinWritten = true;

          writeOptState(true).catch(function (error) {
            console.error('Brewapp: could not set the default opt-in', error);
          });
        }
      }
    );
  }

  if (!customElements.get('brewapp-optout')) {
    customElements.define(
      'brewapp-optout',
      class BrewappOptout extends HTMLElement {
        connectedCallback() {
          this.listeners = new AbortController();

          this.button = this.querySelector('.no-protection-checkout-link');
          if (!this.button) return;

          this.button.addEventListener('click', this.decline.bind(this), {
            signal: this.listeners.signal
          });
        }

        disconnectedCallback() {
          if (this.listeners) this.listeners.abort();
        }

        decline() {
          this.button.disabled = true;

          /* Already declined earlier in this cart — no need to write it again. */
          if (this.dataset.optState === 'false') {
            goToCheckout();
            return;
          }

          writeOptState(false)
            .catch(function (error) {
              console.error('Brewapp: could not record the opt-out', error);
            })
            .then(goToCheckout);
        }
      }
    );
  }
})();
